import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

export default function MemberQR() {
  const { qr_token } = useParams();

  const [member, setMember] = useState(null);
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchMember = async () => {
      try {
        const response = await fetch(`/api/gym/member/public/qr/${qr_token}/`, {
          method: "GET",
        });

        const data = await response.json();

        if (!response.ok) {
          setBanner({
            type: "error",
            message: data.error || "No se pudo cargar la información del usuario",
          });
          setLoading(false);
          return;
        }

        setMember(data);
      } catch {
        setBanner({
          type: "error",
          message: "Ocurrió un error al conectar con el servidor",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [qr_token]);

  const profilePhoto = member?.profile?.profile_photo || "";
  const fullName = member
    ? `${member.first_name || ""} ${member.last_name || ""}`.trim()
    : "";
  const accountStatusLabel = member?.is_active ? "Acceso activo" : "Acceso inactivo";
  const accountStatusColor = member?.is_active ? "#16a34a" : "#dc2626";
  const membership = member?.active_membership || null;
  const membershipName = membership?.plan_name || "Sin membresía";
  const membershipStatus = membership?.is_currently_active ? "Activa" : "Inactiva";
  const membershipStatusColor = membership?.is_currently_active ? "#16a34a" : "#dc2626";
  const initial =
    fullName?.charAt(0)?.toUpperCase() ||
    member?.email?.charAt(0)?.toUpperCase() ||
    "U";

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#ffffff",
        py: { xs: 4, md: 6 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "92%", md: "78%", lg: "70%" }, mx: "auto" }}
      >
        <Box
          sx={{
            borderRadius: { xs: "24px", md: "28px" },
            border: "1px solid rgba(17,17,17,0.08)",
            bgcolor: "#ffffff",
            boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.4, sm: 3, md: 4 },
              pt: { xs: 2.8, md: 3.4 },
              pb: { xs: 2.2, md: 2.6 },
              borderBottom: "1px solid rgba(17,17,17,0.06)",
              background:
                "linear-gradient(180deg, rgba(17,17,17,0.03) 0%, rgba(17,17,17,0.00) 100%)",
            }}
          >
            <Typography
              sx={{
                color: "#111111",
                fontWeight: 900,
                lineHeight: 1.02,
                letterSpacing: -1.1,
                fontSize: {
                  xs: "clamp(28px, 8vw, 38px)",
                  md: "clamp(34px, 3vw, 48px)",
                },
                mb: 1,
              }}
            >
              Perfil del miembro
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.68)",
                lineHeight: 1.75,
                fontSize: { xs: 14.8, md: 15.8 },
                fontWeight: 500,
                maxWidth: 720,
              }}
            >
              Verificación rápida del usuario escaneado y estado actual de su membresía.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2.4, sm: 3, md: 4 } }}>
            <Stack spacing={3}>
              {banner.message && (
                <Alert severity={banner.type} sx={{ borderRadius: "14px" }}>
                  {banner.message}
                </Alert>
              )}

              {loading ? (
                <Box
                  sx={{
                    minHeight: "320px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                  <Typography
                    sx={{
                      color: "rgba(17,17,17,0.72)",
                      fontSize: 16,
                      fontWeight: 600,
                    }}
                  >
                    Cargando información del miembro...
                  </Typography>
                </Box>
              ) : member ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "340px 1fr" },
                    gap: 3,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: "24px",
                      border: "1px solid rgba(17,17,17,0.08)",
                      p: { xs: 2.4, md: 3 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 1.6,
                      background:
                        "linear-gradient(180deg, rgba(17,17,17,0.02) 0%, rgba(17,17,17,0.00) 100%)",
                    }}
                  >
                    <Avatar
                      src={profilePhoto || undefined}
                      alt={fullName}
                      sx={{
                        width: 120,
                        height: 120,
                        fontSize: 38,
                        fontWeight: 800,
                        bgcolor: "#111111",
                        color: "#ffffff",
                        boxShadow: "0 16px 36px rgba(0,0,0,0.16)",
                      }}
                    >
                      {initial}
                    </Avatar>

                    <Stack spacing={0.7} alignItems="center">
                      <Typography
                        sx={{
                          color: "#111111",
                          fontWeight: 900,
                          fontSize: { xs: 22, md: 24 },
                          lineHeight: 1.1,
                        }}
                      >
                        {fullName || "No disponible"}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(17,17,17,0.56)",
                          fontWeight: 700,
                          fontSize: 14,
                          textTransform: "uppercase",
                          letterSpacing: 0.7,
                        }}
                      >
                        {member.role || "member"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                      <Chip
                        label={accountStatusLabel}
                        sx={{
                          px: 1,
                          height: 34,
                          borderRadius: "999px",
                          bgcolor: accountStatusColor,
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 2.2,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(17,17,17,0.52)",
                          fontSize: 12.5,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          mb: 0.8,
                        }}
                      >
                        Nombre completo
                      </Typography>
                      <Typography
                        sx={{
                          color: "#111111",
                          fontSize: 17,
                          fontWeight: 800,
                          lineHeight: 1.35,
                          wordBreak: "break-word",
                        }}
                      >
                        {fullName || "No disponible"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 2.2,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(17,17,17,0.52)",
                          fontSize: 12.5,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          mb: 0.8,
                        }}
                      >
                        Correo
                      </Typography>
                      <Typography
                        sx={{
                          color: "#111111",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                          wordBreak: "break-word",
                        }}
                      >
                        {member.email || "No disponible"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 2.2,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(17,17,17,0.52)",
                          fontSize: 12.5,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          mb: 0.8,
                        }}
                      >
                        Teléfono
                      </Typography>
                      <Typography
                        sx={{
                          color: "#111111",
                          fontSize: 16,
                          fontWeight: 700,
                          lineHeight: 1.5,
                        }}
                      >
                        {member.phone || "No disponible"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 2.2,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(17,17,17,0.52)",
                          fontSize: 12.5,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          mb: 0.8,
                        }}
                      >
                        Membresía
                      </Typography>
                      <Typography
                        sx={{
                          color: "#111111",
                          fontSize: 17,
                          fontWeight: 800,
                          lineHeight: 1.35,
                          wordBreak: "break-word",
                        }}
                      >
                        {membershipName}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        gridColumn: { xs: "auto", sm: "1 / -1" },
                        borderRadius: "22px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: { xs: 2.2, md: 2.6 },
                        bgcolor: membership?.is_currently_active
                          ? "rgba(22,163,74,0.06)"
                          : "rgba(220,38,38,0.05)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.4}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            sx={{
                              color: "rgba(17,17,17,0.52)",
                              fontSize: 12.5,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: 0.6,
                              mb: 0.8,
                            }}
                          >
                            Estado de la membresía
                          </Typography>

                          <Typography
                            sx={{
                              color: "#111111",
                              fontSize: { xs: 18, md: 20 },
                              fontWeight: 900,
                              lineHeight: 1.2,
                            }}
                          >
                            {membership?.is_currently_active
                              ? "Membresía activa"
                              : "Membresía inactiva"}
                          </Typography>

                          <Typography
                            sx={{
                              color: "rgba(17,17,17,0.64)",
                              fontSize: 14.5,
                              fontWeight: 600,
                              mt: 0.6,
                              lineHeight: 1.5,
                            }}
                          >
                            {membership
                              ? "El miembro cuenta con una membresía registrada en el sistema."
                              : "El miembro no tiene una membresía activa registrada actualmente."}
                          </Typography>
                        </Box>

                        <Chip
                          label={membership?.is_currently_active ? "Activa" : "Inactiva"}
                          sx={{
                            px: 1.2,
                            height: 38,
                            borderRadius: "999px",
                            bgcolor: membership?.is_currently_active ? "#16a34a" : "#dc2626",
                            color: "#ffffff",
                            fontWeight: 800,
                            fontSize: 13.5,
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}